/**
 * Scenario sensitivity tornado — pure one-at-a-time attribute perturbation
 * over the SAME `scenarioEngine` kernel `/compare` uses (SIL-299, W3,
 * financials-hifi-flow-spec.md slice 4 "swing on net").
 *
 * Compute discipline (spec slice 4 — "never loop the resolver"): the caller
 * (`apps/api/src/lib/scenario-sensitivity.ts`) resolves cohort aggregates
 * ONCE (same `buildCohortInputs` path `/compare` uses) producing a base
 * `ScenarioCohortInput[]`; `computeSensitivityTornado` below only perturbs
 * that already-resolved, in-memory input and re-runs the pure
 * `scenarioEngine` — 4 attributes x 2 bounds = 8 engine runs total, never a
 * second resolver pass.
 *
 * `SENSITIVITY_ATTRIBUTES` is a **plain array literal** of `{id, label,
 * resolveBounds, apply}` (spec: "no registry framing") — a 5th/6th
 * attribute is just another array entry, not a new abstraction.
 *
 * No DB access, no async — matches every other kernel in this file's
 * sibling modules (`scenario-engine.ts`, `power-cost.ts`).
 */

import { scenarioEngine } from './scenario-engine.js';
import type { ScenarioCohortInput, ScenarioEngineInputs } from './scenario-engine.js';
import { CLIENT_OUTLOOK_CONE_PCT } from './fmv-forecast.js';
import {
  SENSITIVITY_KWH_BOUND_PCT,
  SENSITIVITY_RESIDUAL_RECOVERY_BOUND_PTS,
  SENSITIVITY_PUE_BOUND_PCT,
} from './financials-constants.js';
import { basisOf } from './dto/financials.js';
import type { BasisEnvelope, BasisSource, CalculationBasis, CalculationBasisInput } from './dto/financials.js';
import type { SensitivityAttributeId } from './dto/scenario-sensitivity.js';
import type { ProjectedUsePhaseKgCo2e } from './dto/use-phase.js';
import {
  projectScenarioUsePhaseKgco2e,
  resolveCarbonAccrualWindow,
  sumScenarioUsePhaseProjections,
  SCENARIO_USE_PHASE_PUE_MIN,
  SCENARIO_USE_PHASE_PUE_MAX,
} from './use-phase/scenario-use-phase.js';

/** `formulaId` for the sensitivity tornado's per-attribute basis — defined
 * here, beside the kernel it names (spec Cross-cutting "formulaId ...
 * defined beside the kernel it names"). */
export const SENSITIVITY_ENGINE_FORMULA_ID = 'scenario_engine.sensitivity.v1';

const round2 = (n: number): number => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// Context — everything resolveBounds/apply need, all already resolved by
// the API layer (no DB access from this module).
// ---------------------------------------------------------------------------

/** Client-safe [p25, p75] band for the SKU a scenario's refresh cohort(s)
 * target — resolved from `sku_pricing_snapshot` by the API layer (same
 * table `market-trends.ts` reads). `suppressed: true` means the comp count
 * fell below `COMPS_K_ANONYMITY_FLOOR` — the attribute is inapplicable in
 * that case (spec: "k-floor applies"), never silently computed off a
 * sub-floor sample. */
export interface RefreshStreetPriceBand {
  p25: number;
  p75: number;
  suppressed: boolean;
}

export interface SensitivityContext {
  /** The scenario's full resolved cohort set (every stored cohort's
   * `buildCohortInputs` output, concatenated) — the SAME array
   * `scenarioEngine` runs the baseline compute against. */
  cohorts: ScenarioCohortInput[];
  kwhUsd: number;
  /** The scenario's stored/overridden ITAD recovery %, 0-100. */
  itadRecoveryPct: number;
  /** Null when the scenario has no `refresh` cohort — the street-price
   * attribute reports `applicable: false` in that case. */
  refreshStreetPriceBand: RefreshStreetPriceBand | null;
  /** The PUE actually applied to the carbon baseline — already resolved by
   * the API layer (`resolveScenarioUsePhasePue`): the scenario's own
   * validated `[1.0, 3.0]` value, or the published industry default. Never
   * null — `applyPue` throws on null, so the caller resolves this before
   * building the context. */
  resolvedPue: number;
}

export interface SensitivityBounds {
  low: number;
  high: number;
}

export type SensitivityBoundsResult =
  | { applicable: true; bounds: SensitivityBounds; source: BasisSource }
  | { applicable: false; reason: string };

export interface SensitivityAttribute {
  id: SensitivityAttributeId;
  label: string;
  /** Pure — returns the [low, high] bound pair to perturb this attribute
   * over, or `{applicable: false, reason}` when the scenario has no cohort
   * this attribute meaningfully affects (spec: "0 swing must never read as
   * insensitive" — an inapplicable attribute is never silently computed as
   * a real, zero-swing bar). */
  resolveBounds(ctx: SensitivityContext): SensitivityBoundsResult;
  /** Pure — returns a NEW `ScenarioEngineInputs` with exactly this
   * attribute perturbed to `bound`, every other input untouched. Never
   * mutates `inputs`. */
  apply(inputs: ScenarioEngineInputs, bound: number): ScenarioEngineInputs;
}

// ---------------------------------------------------------------------------
// Shared perturbation helpers
// ---------------------------------------------------------------------------

/** Scales a cohort's FMV-derived figures by a fractional delta (e.g. +0.15
 * = the CLIENT_OUTLOOK_CONE_PCT "high" band). `itadBaselineUsd` scales
 * proportionally with `currentFmvUsd` — it is always
 * `currentFmvUsd * (itadRecoveryPct / 100)` upstream, so scaling both by
 * the same factor keeps them consistent without needing the pct itself
 * back here. */
function scaleCohortFmv(cohort: ScenarioCohortInput, deltaFrac: number): ScenarioCohortInput {
  return {
    ...cohort,
    currentFmvUsd: round2(cohort.currentFmvUsd * (1 + deltaFrac)),
    itadBaselineUsd: round2(cohort.itadBaselineUsd * (1 + deltaFrac)),
  };
}

// ---------------------------------------------------------------------------
// The 4 attributes (spec slice 4) — plain array literal, no registry.
// ---------------------------------------------------------------------------

export const SENSITIVITY_ATTRIBUTES: SensitivityAttribute[] = [
  {
    id: 'refresh_street_price',
    label: 'Refresh SKU market price',
    resolveBounds(ctx) {
      const hasRefresh = ctx.cohorts.some((c) => c.action === 'refresh' && c.refresh);
      if (!hasRefresh) {
        return { applicable: false, reason: 'Scenario has no refresh cohort — market price has no effect' };
      }
      if (!ctx.refreshStreetPriceBand) {
        return { applicable: false, reason: 'No market comps found for the refresh SKU' };
      }
      if (ctx.refreshStreetPriceBand.suppressed) {
        return {
          applicable: false,
          reason: 'Insufficient market comps for the refresh SKU to compute a bound (below the k-anonymity floor)',
        };
      }
      return {
        applicable: true,
        bounds: { low: ctx.refreshStreetPriceBand.p25, high: ctx.refreshStreetPriceBand.p75 },
        source: 'market_comps',
      };
    },
    apply(inputs, bound) {
      // `bound` is an absolute target list price for the FIRST refresh
      // cohort's SKU (the common case: a scenario has one refresh target).
      // Every refresh cohort is scaled by the SAME ratio off its own base
      // price, so a rare multi-refresh-SKU scenario still perturbs
      // proportionally rather than pinning every cohort to one SKU's
      // absolute dollar figure.
      const primary = inputs.cohorts.find((c) => c.action === 'refresh' && c.refresh);
      if (!primary?.refresh || primary.refresh.newSkuListPriceUsd <= 0) return inputs;
      const ratio = bound / primary.refresh.newSkuListPriceUsd;
      return {
        ...inputs,
        cohorts: inputs.cohorts.map((c) => {
          if (c.action !== 'refresh' || !c.refresh) return c;
          return {
            ...c,
            refresh: {
              ...c.refresh,
              newSkuListPriceUsd: round2(c.refresh.newSkuListPriceUsd * ratio),
              newFmvUsd: round2(c.refresh.newFmvUsd * ratio),
            },
          };
        }),
      };
    },
  },
  {
    id: 'power_kwh',
    label: 'Power cost ($/kWh)',
    resolveBounds(ctx) {
      if (ctx.cohorts.length === 0) {
        return { applicable: false, reason: 'Scenario has no cohorts to evaluate' };
      }
      const pct = SENSITIVITY_KWH_BOUND_PCT / 100;
      return {
        applicable: true,
        bounds: { low: round2(ctx.kwhUsd * (1 - pct)), high: round2(ctx.kwhUsd * (1 + pct)) },
        source: 'manual_entry',
      };
    },
    apply(inputs, bound) {
      return { ...inputs, kwhUsd: bound };
    },
  },
  {
    id: 'fmv_band',
    label: 'FMV band (low/high)',
    resolveBounds(ctx) {
      if (ctx.cohorts.length === 0) {
        return { applicable: false, reason: 'Scenario has no cohorts to evaluate' };
      }
      // Same fixed symmetric cone the client FMV Outlook card / /compare's
      // client-safe band use (CLIENT_OUTLOOK_CONE_PCT) — never the ops-only
      // stored fmv_low_usd/fmv_high_usd columns (RUF-557 "578 doctrine").
      return {
        applicable: true,
        bounds: { low: -CLIENT_OUTLOOK_CONE_PCT, high: CLIENT_OUTLOOK_CONE_PCT },
        source: 'fmv_snapshot',
      };
    },
    apply(inputs, bound) {
      return { ...inputs, cohorts: inputs.cohorts.map((c) => scaleCohortFmv(c, bound)) };
    },
  },
  {
    id: 'pue',
    label: 'PUE (facility overhead)',
    resolveBounds(ctx) {
      if (ctx.cohorts.length === 0) {
        return { applicable: false, reason: 'Scenario has no cohorts to evaluate' };
      }
      const pct = SENSITIVITY_PUE_BOUND_PCT / 100;
      const low = Math.max(SCENARIO_USE_PHASE_PUE_MIN, round2(ctx.resolvedPue * (1 - pct)));
      const high = Math.min(SCENARIO_USE_PHASE_PUE_MAX, round2(ctx.resolvedPue * (1 + pct)));
      return { applicable: true, bounds: { low, high }, source: 'manual_entry' };
    },
    apply(inputs, bound) {
      return { ...inputs, pue: bound };
    },
  },
  {
    id: 'residual_recovery',
    label: 'Residual recovery %',
    resolveBounds(ctx) {
      const hasItad = ctx.cohorts.some((c) => c.action === 'itad');
      if (!hasItad) {
        return { applicable: false, reason: 'Scenario has no ITAD cohort — residual recovery % has no effect' };
      }
      const pts = SENSITIVITY_RESIDUAL_RECOVERY_BOUND_PTS;
      return {
        applicable: true,
        bounds: {
          low: Math.max(0, ctx.itadRecoveryPct - pts),
          high: Math.min(100, ctx.itadRecoveryPct + pts),
        },
        source: 'manual_entry',
      };
    },
    apply(inputs, bound) {
      return {
        ...inputs,
        cohorts: inputs.cohorts.map((c) =>
          c.action === 'itad' ? { ...c, itadBaselineUsd: round2(c.currentFmvUsd * (bound / 100)) } : c,
        ),
      };
    },
  },
];

// ---------------------------------------------------------------------------
// Basis description (assembly seam, spec Cross-cutting)
// ---------------------------------------------------------------------------

export interface SensitivityAttributeBasisInputs {
  lowBound: BasisEnvelope;
  highBound: BasisEnvelope;
}

/** @pure */
export function describeSensitivityAttributeBasis(inputs: SensitivityAttributeBasisInputs): CalculationBasisInput[] {
  return (Object.keys(inputs) as Array<keyof SensitivityAttributeBasisInputs>).map((name) => ({
    name,
    ...inputs[name],
  }));
}

// ---------------------------------------------------------------------------
// Tornado compute — 1 base run + (applicable attributes) x 2 runs.
// ---------------------------------------------------------------------------

export interface SensitivityAttributeCompute {
  id: SensitivityAttributeId;
  label: string;
  applicable: boolean;
  reason: string | null;
  baseNetUsd: number;
  lowNetUsd: number | null;
  highNetUsd: number | null;
  swingUsd: number | null;
  /** SIL-346 (T8) — see the DTO field docstrings (`dto/scenario-sensitivity.ts`)
   * for why only the `pue` attribute moves this away from `baseCarbonKgco2e`. */
  baseCarbonKgco2e: ProjectedUsePhaseKgCo2e;
  lowCarbonKgco2e: ProjectedUsePhaseKgCo2e | null;
  highCarbonKgco2e: ProjectedUsePhaseKgCo2e | null;
  swingKgco2e: number | null;
  basis: CalculationBasis;
}

const CONSTANTS_BY_ATTRIBUTE: Partial<Record<SensitivityAttributeId, 'sensitivityKwhBoundPct' | 'sensitivityResidualRecoveryBoundPts' | 'compsKAnonymityFloor' | 'sensitivityPueBoundPct'>> = {
  power_kwh: 'sensitivityKwhBoundPct',
  residual_recovery: 'sensitivityResidualRecoveryBoundPts',
  refresh_street_price: 'compsKAnonymityFloor',
  pue: 'sensitivityPueBoundPct',
};

/**
 * Projects `ctx.totalDeviceAnnualRateKgco2eqPerYr` at a given PUE over the
 * scenario's horizon — the use-phase-carbon analog of `scenarioEngine`'s
 * dollar run, called with whichever `pue` a given attribute's perturbation
 * (or the unperturbed base) implies. Only the `pue` attribute below ever
 * calls this with anything other than `ctx.resolvedPue` — see this
 * function's callers.
 */
function carbonAt(
  ctx: SensitivityContext,
  horizonMonths: number,
  pue: number,
): ProjectedUsePhaseKgCo2e {
  // Staged by cohort action/timing (SIL-346) — the SAME per-cohort
  // resolveCarbonAccrualWindow the /compare path uses
  // (apps/api/src/lib/scenario-compare.ts's resolveScenarioUseCarbon), so a
  // sell/itad/refresh cohort's carbon here matches /compare's exactly. A
  // flat `totalRate * horizonMonths/12` would report identical carbon for
  // "sell at month 3" and "hold for the full horizon" — the same defect
  // this ticket's compare-path fix guards against, reached through a
  // different endpoint.
  const perCohort = ctx.cohorts.map((cohort) => {
    const rate = cohort.useCarbonDeviceAnnualRateKgco2eqPerYr ?? 0;
    // SIL-571 — `buy` isn't in `ScenarioActionForCarbonAccrual` (it carries
    // no use-phase device rate; `useCarbonDeviceAnnualRateKgco2eqPerYr` is
    // always 0 for a buy cohort, see buildBuyCohortInput), so the accrual
    // window it resolves to is moot — mapped to `hold` (accrues the full
    // horizon) purely to satisfy the type, never read as a real rate.
    const { accrualMonths } = resolveCarbonAccrualWindow(
      cohort.action === 'buy' ? 'hold' : cohort.action,
      cohort.timingMonths,
      horizonMonths,
    );
    return projectScenarioUsePhaseKgco2e(rate, pue, accrualMonths);
  });
  return sumScenarioUsePhaseProjections(perCohort);
}

/**
 * "Swing on net" — the metric perturbed per attribute is
 * `metrics.delta.netPositionUsd` (scenario vs baseline, horizon-end): the
 * headline "so what" figure `/compare` already surfaces, not either arm in
 * isolation. Runs `scenarioEngine` once for the base (unperturbed) inputs,
 * then twice per applicable attribute (low bound, high bound) — never a
 * third resolver pass, only re-invocations of the pure engine.
 *
 * SIL-346 (T8): every row also carries a use-phase carbon figure computed
 * off the SAME `low`/`high` bound `scenarioEngine` was just run with —
 * for the `pue` attribute that bound IS the perturbed PUE, so carbon moves
 * exactly like dollars; every other attribute's bound has no physical
 * effect on carbon, so its low/high carbon both equal the base (an
 * explicit, disclosed zero swing, never a bare `null`).
 *
 * @pure
 */
export function computeSensitivityTornado(
  baseInputs: ScenarioEngineInputs,
  ctx: SensitivityContext,
  generatedAt: string,
): SensitivityAttributeCompute[] {
  const baseNetUsd = scenarioEngine(baseInputs).metrics.delta.netPositionUsd;
  const baseCarbonKgco2e = carbonAt(ctx, baseInputs.horizonMonths, ctx.resolvedPue);

  return SENSITIVITY_ATTRIBUTES.map((attr) => {
    const boundsResult = attr.resolveBounds(ctx);

    if (!boundsResult.applicable) {
      return {
        id: attr.id,
        label: attr.label,
        applicable: false,
        reason: boundsResult.reason,
        baseNetUsd,
        lowNetUsd: null,
        highNetUsd: null,
        swingUsd: null,
        baseCarbonKgco2e,
        lowCarbonKgco2e: null,
        highCarbonKgco2e: null,
        swingKgco2e: null,
        basis: basisOf({ formulaId: SENSITIVITY_ENGINE_FORMULA_ID, inputs: [] }),
      };
    }

    const { low, high } = boundsResult.bounds;
    const lowNetUsd = scenarioEngine(attr.apply(baseInputs, low)).metrics.delta.netPositionUsd;
    const highNetUsd = scenarioEngine(attr.apply(baseInputs, high)).metrics.delta.netPositionUsd;

    // Only `pue` perturbs a value the carbon kernel reads — every other
    // attribute's bound has no term in `carbonAt`, so passing it through
    // would be a no-op; hard-coding that here (rather than a generic "read
    // `.pue` off the perturbed inputs" trick) keeps the coupling explicit
    // and greppable rather than implicit in `apply()`'s field name.
    const lowCarbonKgco2e = carbonAt(ctx, baseInputs.horizonMonths, attr.id === 'pue' ? low : ctx.resolvedPue);
    const highCarbonKgco2e = carbonAt(ctx, baseInputs.horizonMonths, attr.id === 'pue' ? high : ctx.resolvedPue);

    const constantName = CONSTANTS_BY_ATTRIBUTE[attr.id];
    const basis = basisOf({
      formulaId: SENSITIVITY_ENGINE_FORMULA_ID,
      inputs: describeSensitivityAttributeBasis({
        lowBound: { value: low, source: boundsResult.source, asOf: generatedAt, isFallback: false },
        highBound: { value: high, source: boundsResult.source, asOf: generatedAt, isFallback: false },
      }),
      constants: constantName ? [constantName] : [],
    });

    return {
      id: attr.id,
      label: attr.label,
      applicable: true,
      reason: null,
      baseNetUsd,
      lowNetUsd,
      highNetUsd,
      swingUsd: round2(highNetUsd - lowNetUsd),
      baseCarbonKgco2e,
      lowCarbonKgco2e,
      highCarbonKgco2e,
      swingKgco2e: round2((highCarbonKgco2e as number) - (lowCarbonKgco2e as number)),
      basis,
    };
  });
}
